import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  makeStyles,
  Typography,
  TextField,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  IconButton,
  Divider,
  Tabs,
  Tab,
  Button,
} from "@material-ui/core";
import {
  Close as CloseIcon,
  PlayArrow,
  Settings,
  OpenInNew,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@material-ui/icons";

// Importacoes individuais dos icones usados no NODE_META
// (evita import * que carrega ~2100 icones e trava o dev mode)
import RocketLaunch from "@mui/icons-material/RocketLaunch";
import Webhook from "@mui/icons-material/Webhook";
import Schedule from "@mui/icons-material/Schedule";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import Message from "@mui/icons-material/Message";
import LibraryBooks from "@mui/icons-material/LibraryBooks";
import ImageIcon from "@mui/icons-material/Image";
import MicNone from "@mui/icons-material/MicNone";
import Videocam from "@mui/icons-material/Videocam";
import Description from "@mui/icons-material/Description";
import LocationOn from "@mui/icons-material/LocationOn";
import ContactPhone from "@mui/icons-material/ContactPhone";
import EmojiEmotions from "@mui/icons-material/EmojiEmotions";
import ThumbUp from "@mui/icons-material/ThumbUp";
import DynamicFeed from "@mui/icons-material/DynamicFeed";
import SmartButton from "@mui/icons-material/SmartButton";
import Pix from "@mui/icons-material/Pix";
import ViewList from "@mui/icons-material/ViewList";
import TextFields from "@mui/icons-material/TextFields";
import ImportExport from "@mui/icons-material/ImportExport";
import AccountTree from "@mui/icons-material/AccountTree";
import AltRoute from "@mui/icons-material/AltRoute";
import FilterAlt from "@mui/icons-material/FilterAlt";
import Casino from "@mui/icons-material/Casino";
import AccessTime from "@mui/icons-material/AccessTime";
import HourglassEmpty from "@mui/icons-material/HourglassEmpty";
import CallMerge from "@mui/icons-material/CallMerge";
import Loop from "@mui/icons-material/Loop";
import ViewStream from "@mui/icons-material/ViewStream";
import Cancel from "@mui/icons-material/Cancel";
import ConfirmationNumber from "@mui/icons-material/ConfirmationNumber";
import Label from "@mui/icons-material/Label";
import LabelOff from "@mui/icons-material/LabelOff";
import LocalOffer from "@mui/icons-material/LocalOffer";
import SupportAgent from "@mui/icons-material/SupportAgent";
import Campaign from "@mui/icons-material/Campaign";
import Http from "@mui/icons-material/Http";
import DataObject from "@mui/icons-material/DataObject";
import Transform from "@mui/icons-material/Transform";
import Code from "@mui/icons-material/Code";
import FindReplace from "@mui/icons-material/FindReplace";
import Email from "@mui/icons-material/Email";
import Psychology from "@mui/icons-material/Psychology";
import AutoAwesome from "@mui/icons-material/AutoAwesome";
import SmartToy from "@mui/icons-material/SmartToy";
import Category from "@mui/icons-material/Category";
import StickyNote2 from "@mui/icons-material/StickyNote2";
import PersonSearch from "@mui/icons-material/PersonSearch";
import Receipt from "@mui/icons-material/Receipt";
import LockOpen from "@mui/icons-material/LockOpen";
import Workspaces from "@mui/icons-material/Workspaces";
import HighlightOff from "@mui/icons-material/HighlightOff";
import ViewKanban from "@mui/icons-material/ViewKanban";
import HttpRequestTestPanel from "./components/HttpRequestTestPanel";

// Mapa de nomes de icone -> componente (substitui import * as MuiIcons)
const ICON_MAP = {
  RocketLaunch, Webhook, Schedule, ErrorOutline,
  Message, LibraryBooks, Image: ImageIcon, MicNone, Videocam,
  Description, LocationOn, ContactPhone, EmojiEmotions, ThumbUp,
  DynamicFeed, SmartButton, Pix, ViewList, TextFields,
  ImportExport, AccountTree, AltRoute, FilterAlt, Casino,
  AccessTime, HourglassEmpty, CallMerge, Loop, ViewStream, Cancel,
  ConfirmationNumber, Label, LabelOff, LocalOffer, SupportAgent, Campaign, ViewKanban, HighlightOff,
  Http, DataObject, Transform, Code, FindReplace, Email,
  Psychology, AutoAwesome, SmartToy, Category, StickyNote2,
  PersonSearch, Receipt, LockOpen, Workspaces,
};

const ACCENT = "#9a00ed";
const WIDTH = 340;

const normalizeHttpRequestData = (data = {}) => ({
  ...data,
  url: data.url || "",
  method: data.method || "GET",
  headersString: data.headersString ?? data.headers ?? "",
  requestBody: data.requestBody ?? data.body ?? "",
  headers: data.headersString ?? data.headers ?? "",
  body: data.requestBody ?? data.body ?? "",
  responseVar: data.responseVar || "",
  saveVariables: Array.isArray(data.saveVariables) ? data.saveVariables : [],
  responseVariables: Array.isArray(data.responseVariables) ? data.responseVariables : [],
  queryParams: Array.isArray(data.queryParams) ? data.queryParams : [],
  timeout: data.timeout != null ? data.timeout : 10000,
});

const syncHttpRequestBackendFields = (data, key, value) => {
  const next = { ...data, [key]: value };
  if (key === "headers" || key === "headersString") {
    const headersValue = key === "headersString" ? value : value;
    next.headersString = headersValue;
    next.headers = headersValue;
  }
  if (key === "body" || key === "requestBody") {
    const bodyValue = key === "requestBody" ? value : value;
    next.requestBody = bodyValue;
    next.body = bodyValue;
  }
  if (key === "responseVar") {
    next.responseVar = String(value || "").trim();
  }
  return next;
};

const useStyles = makeStyles((theme) => ({
  root: {
    position: "absolute",
    top: 0,
    right: 0,
    width: WIDTH,
    height: "100%",
    backgroundColor: "#fff",
    borderLeft: `2px solid ${ACCENT}20`,
    boxShadow: "-4px 0 16px rgba(0,0,0,0.06)",
    zIndex: 20,
    display: "flex",
    flexDirection: "column",
  },
  hidden: {
    transform: `translateX(${WIDTH + 10}px)`,
    pointerEvents: "none",
    visibility: "hidden",
    // Visibility muda APOS a animacao de saida terminar (0.25s delay)
    transition: "transform 0.25s cubic-bezier(0.4,0,0.2,1), visibility 0s linear 0.25s",
  },
  visible: {
    transform: "translateX(0)",
    pointerEvents: "auto",
    visibility: "visible",
    // Visibility muda IMEDIATAMENTE ao abrir (0s delay)
    transition: "transform 0.25s cubic-bezier(0.4,0,0.2,1), visibility 0s linear 0s",
  },
  header: {
    display: "flex",
    alignItems: "center",
    padding: "12px 14px",
    borderBottom: `1px solid #eee`,
    gap: 8,
  },
  headerIcon: {
    color: ACCENT,
    fontSize: 22,
  },
  headerTitle: {
    flex: 1,
    fontSize: 14,
    fontWeight: 600,
    color: "#1a1a1a",
  },
  tabsRoot: {
    minHeight: 36,
    borderBottom: "1px solid #eee",
  },
  tab: {
    minHeight: 36,
    fontSize: 12,
    textTransform: "none",
    fontWeight: 500,
  },
  tabIndicator: {
    backgroundColor: ACCENT,
  },
  content: {
    flex: 1,
    overflowY: "auto",
    padding: "14px 14px 20px",
  },
  field: {
    marginBottom: 14,
  },
  readOnly: {
    "& .MuiInputBase-input": {
      color: "#999",
    },
  },
  openLegacyBtn: {
    marginTop: 10,
    textTransform: "none",
    fontWeight: 500,
    borderColor: ACCENT,
    color: ACCENT,
    "&:hover": {
      backgroundColor: `${ACCENT}08`,
    },
  },
  optionRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 6,
  },
  optionNum: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    backgroundColor: `${ACCENT}15`,
    color: ACCENT,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 700,
    flexShrink: 0,
  },
  colorPicker: {
    width: 40,
    height: 40,
    border: "2px solid #ddd",
    borderRadius: 8,
    cursor: "pointer",
    padding: 0,
    background: "none",
    "&::-webkit-color-swatch-wrapper": { padding: 0 },
    "&::-webkit-color-swatch": { border: "none", borderRadius: 6 },
  },
}));

/**
 * Resolve um nome de icone MUI para o componente React.
 * Usa ICON_MAP (imports individuais) ao inves de import * para performance.
 * Retorna <Settings /> como fallback.
 */
const resolveIcon = (name) => {
  const Icon = ICON_MAP[name];
  return Icon ? <Icon /> : <Settings />;
};

// Mapa tipo do node -> { icone, label } — Todos os 52 tipos de node
const NODE_META = {
  // --- Inicio ---
  start:            { icon: resolveIcon("RocketLaunch"),      label: "Inicio" },
  webhookTrigger:   { icon: resolveIcon("Webhook"),           label: "Webhook Trigger" },
  scheduleTrigger:  { icon: resolveIcon("Schedule"),          label: "Agendamento" },
  errorTrigger:     { icon: resolveIcon("ErrorOutline"),      label: "Trigger de Erro" },
  // --- Mensagens ---
  message:          { icon: resolveIcon("Message"),           label: "Mensagem" },
  singleBlock:      { icon: resolveIcon("LibraryBooks"),      label: "Conteudo" },
  img:              { icon: resolveIcon("Image"),             label: "Imagem" },
  audio:            { icon: resolveIcon("MicNone"),           label: "Audio" },
  video:            { icon: resolveIcon("Videocam"),          label: "Video" },
  sendTemplate:     { icon: resolveIcon("Description"),       label: "Template HSM" },
  sendLocation:     { icon: resolveIcon("LocationOn"),        label: "Localizacao" },
  sendContact:      { icon: resolveIcon("ContactPhone"),      label: "Contato (vCard)" },
  sendSticker:      { icon: resolveIcon("EmojiEmotions"),     label: "Sticker" },
  sendReaction:     { icon: resolveIcon("ThumbUp"),           label: "Reacao" },
  // --- Interacao ---
  menu:             { icon: resolveIcon("DynamicFeed"),       label: "Menu" },
  button:           { icon: resolveIcon("SmartButton"),       label: "Botoes" },
  buttonPix:        { icon: resolveIcon("Pix"),               label: "Botao PIX" },
  list:             { icon: resolveIcon("ViewList"),          label: "Lista" },
  input:            { icon: resolveIcon("TextFields"),        label: "Captura de Entrada" },
  // --- Logica ---
  condition:        { icon: resolveIcon("ImportExport"),      label: "Condicao" },
  ifAdvanced:       { icon: resolveIcon("AccountTree"),       label: "IF Avancado" },
  switch:           { icon: resolveIcon("AltRoute"),          label: "Switch" },
  filter:           { icon: resolveIcon("FilterAlt"),         label: "Filtro" },
  randomizer:       { icon: resolveIcon("Casino"),            label: "Randomizador" },
  interval:         { icon: resolveIcon("AccessTime"),        label: "Intervalo" },
  wait:             { icon: resolveIcon("HourglassEmpty"),    label: "Aguardar" },
  merge:            { icon: resolveIcon("CallMerge"),         label: "Unir Caminhos" },
  loop:             { icon: resolveIcon("Loop"),              label: "Loop" },
  splitBatch:       { icon: resolveIcon("ViewStream"),        label: "Lotes" },
  stopError:        { icon: resolveIcon("Cancel"),            label: "Parar com Erro" },
  switchFlow:       { icon: resolveIcon("AccountTree"),       label: "Trocar Fluxo" },
  // --- Acoes ---
  ticket:           { icon: resolveIcon("ConfirmationNumber"),label: "Transferir Fila" },
  tag:              { icon: resolveIcon("Label"),             label: "Adicionar Tag" },
  removeTag:        { icon: resolveIcon("LabelOff"),          label: "Remover Tag" },
  tagCheck:         { icon: resolveIcon("LocalOffer"),        label: "Verificar Tag" },
  attendant:        { icon: resolveIcon("SupportAgent"),      label: "Atendente" },
  campaignPro:      { icon: resolveIcon("Campaign"),          label: "Campanha Pro" },
  kanbanLane:       { icon: resolveIcon("ViewKanban"),        label: "Kanban Lane" },
  closeTicket:      { icon: resolveIcon("HighlightOff"),      label: "Fechar Ticket" },
  // --- Integracao ---
  httpRequest:      { icon: resolveIcon("Http"),              label: "Requisicao HTTP" },
  variable:         { icon: resolveIcon("DataObject"),        label: "Variavel" },
  setAdvanced:      { icon: resolveIcon("Transform"),         label: "Set Avancado" },
  code:             { icon: resolveIcon("Code"),              label: "Codigo JS" },
  dateTime:         { icon: resolveIcon("AccessTime"),        label: "Data/Hora" },
  text:             { icon: resolveIcon("TextFields"),        label: "Texto" },
  regex:            { icon: resolveIcon("FindReplace"),       label: "Regex" },
  emailSmtp:        { icon: resolveIcon("Email"),             label: "Email SMTP" },
  // --- IA ---
  openai:           { icon: resolveIcon("Psychology"),        label: "OpenAI" },
  gemini:           { icon: resolveIcon("AutoAwesome"),       label: "Gemini" },
  aiAgent:          { icon: resolveIcon("SmartToy"),          label: "Agente IA" },
  textClassifier:   { icon: resolveIcon("Category"),          label: "Classificador" },
  // --- Documentacao ---
  stickyNote:       { icon: resolveIcon("StickyNote2"),       label: "Nota Adesiva" },
  group:            { icon: resolveIcon("Workspaces"),        label: "Grupo" },
  // --- IXC ---
  ixcBuscarCliente: { icon: resolveIcon("PersonSearch"),      label: "IXC Buscar Cliente" },
  ixcBuscarFatura:  { icon: resolveIcon("Receipt"),           label: "IXC Buscar Fatura" },
  ixcDesbloquear:   { icon: resolveIcon("LockOpen"),          label: "IXC Desbloquear" },
};

// Nodes que possuem modal V1 implementado — SOMENTE estes mostram "Abrir editor completo"
const COMPLEX_NODES = [
  "message", "interval", "condition", "menu", "button", "buttonPix",
  "list", "img", "audio", "video", "randomizer", "singleBlock",
  "ticket", "tag", "removeTag", "tagCheck", "switchFlow", "attendant",
  "openai", "gemini", "input", "campaignPro", "kanbanLane", "closeTicket",
];

const CONDITION_OPS = [
  { value: "==", label: "Igual (==)" },
  { value: ">=", label: "Maior ou igual (>=)" },
  { value: "<=", label: "Menor ou igual (<=)" },
  { value: ">", label: "Maior (>)" },
  { value: "<", label: "Menor (<)" },
];

export default function Inspector({
  node,
  onClose,
  onUpdateNode,
  onOpenLegacyModal,
}) {
  const classes = useStyles();
  const [tab, setTab] = useState(0);
  const [localData, setLocalData] = useState({});

  const isOpen = Boolean(node);
  const nodeType = node?.type || "";
  const meta = NODE_META[nodeType] || { icon: <Settings />, label: nodeType };

  // Reseta a aba quando seleciona um node DIFERENTE
  useEffect(() => {
    if (node) {
      setTab(0);
    }
  }, [node?.id]);

  // Sincroniza dados locais quando os dados do node mudam externamente (ex: salvar modal)
  // mas NAO quando nos mesmos disparamos a mudanca via updateField
  const isLocalUpdate = useRef(false);
  useEffect(() => {
    if (node && !isLocalUpdate.current) {
      const data = { ...(node.data || {}) };
      setLocalData(
        node.type === "httpRequest" ? normalizeHttpRequestData(data) : data
      );
    }
    isLocalUpdate.current = false;
  }, [node?.id, node?.data]);

  const updateField = useCallback(
    (key, value) => {
      isLocalUpdate.current = true;
      setLocalData((prev) => {
        let updated =
          nodeType === "httpRequest"
            ? syncHttpRequestBackendFields({ ...prev, [key]: value }, key, value)
            : { ...prev, [key]: value };
        if (node) {
          onUpdateNode(node.id, updated);
        }
        return updated;
      });
    },
    [node, nodeType, onUpdateNode]
  );

  const updateHttpRequestData = useCallback(
    (patch) => {
      isLocalUpdate.current = true;
      setLocalData((prev) => {
        const updated = normalizeHttpRequestData({ ...prev, ...patch });
        if (node) {
          onUpdateNode(node.id, updated);
        }
        return updated;
      });
    },
    [node, onUpdateNode]
  );

  // ------- Botao "Abrir editor completo" -------
  // So exibe para nodes que TEM modal V1 implementado
  const renderOpenEditorButton = () => {
    if (!COMPLEX_NODES.includes(nodeType)) return null;
    return (
      <Button
        variant="outlined"
        className={classes.openLegacyBtn}
        startIcon={<OpenInNew />}
        fullWidth
        onClick={() => onOpenLegacyModal && onOpenLegacyModal(nodeType, node)}
      >
        Abrir editor completo
      </Button>
    );
  };

  // ------- Aba 1: Configuracao (campos por tipo de node) -------
  const renderConfig = () => {
    // ============== Inicio / Triggers ==============
    if (nodeType === "start") {
      return (
        <Typography variant="body2" style={{ color: "#999" }}>
          Node inicial do fluxo. Sem configuracao.
        </Typography>
      );
    }

    if (nodeType === "errorTrigger") {
      return (
        <Typography variant="body2" style={{ color: "#999" }}>
          Dispara quando ocorre um erro em qualquer node. Sem configuracao adicional.
        </Typography>
      );
    }

    // ============== Integracao — somente inline ==============
    if (nodeType === "httpRequest") {
      return (
        <>
          <TextField className={classes.field} label="URL" variant="outlined" size="small" fullWidth
            value={localData.url || ""} onChange={(e) => updateField("url", e.target.value)} />
          <Select className={classes.field} variant="outlined" fullWidth
            value={localData.method || "GET"} onChange={(e) => updateField("method", e.target.value)}
            style={{ marginBottom: 14 }}>
            {["GET", "POST", "PUT", "DELETE", "PATCH"].map((m) => (
              <MenuItem key={m} value={m}>{m}</MenuItem>
            ))}
          </Select>
          <TextField className={classes.field} label="Headers (JSON)" variant="outlined" size="small" fullWidth multiline rows={2}
            value={localData.headersString || ""} onChange={(e) => updateField("headersString", e.target.value)}
            placeholder='{"Authorization": "Bearer ..."}' />
          <TextField className={classes.field} label="Body (JSON)" variant="outlined" size="small" fullWidth multiline rows={3}
            value={localData.requestBody || ""} onChange={(e) => updateField("requestBody", e.target.value)} />
          <TextField className={classes.field} label="Salvar resposta em" variant="outlined" size="small" fullWidth
            value={localData.responseVar || ""} onChange={(e) => updateField("responseVar", e.target.value)}
            placeholder="Nome da variavel (ex: teste)"
            helperText="Grava o JSON completo da resposta. Use ${nome} ou ${nome.campo} nas mensagens." />
          <HttpRequestTestPanel data={localData} onUpdateData={updateHttpRequestData} />
        </>
      );
    }

    if (nodeType === "variable") {
      return (
        <>
          <TextField className={classes.field} label="Nome da variavel" variant="outlined" size="small" fullWidth
            value={localData.variableName || ""} onChange={(e) => updateField("variableName", e.target.value)} />
          <TextField className={classes.field} label="Valor" variant="outlined" size="small" fullWidth
            value={localData.variableValue || ""} onChange={(e) => updateField("variableValue", e.target.value)}
            placeholder="${outraVariavel} ou texto fixo"
            helperText="Use ${variavel} para referenciar outra variavel do fluxo." />
        </>
      );
    }

    if (nodeType === "setAdvanced") {
      return (
        <>
          <Typography variant="caption" style={{ color: "#666", display: "block", marginBottom: 8 }}>
            Define multiplas variaveis de uma vez
          </Typography>
          <TextField className={classes.field} label="Campos (JSON)" variant="outlined" size="small" fullWidth multiline rows={4}
            value={localData.fields ? (typeof localData.fields === "string" ? localData.fields : JSON.stringify(localData.fields, null, 2)) : ""}
            onChange={(e) => updateField("fields", e.target.value)}
            placeholder='{"nome": "valor", "outro": "{{variavel}}"}' />
          <TextField className={classes.field} label="Modo" variant="outlined" size="small" fullWidth
            value={localData.mode || "set"} onChange={(e) => updateField("mode", e.target.value)}
            helperText="set = definir, append = concatenar" />
        </>
      );
    }

    if (nodeType === "code") {
      return (
        <>
          <Typography variant="caption" style={{ color: "#666", display: "block", marginBottom: 8 }}>
            Execute codigo JavaScript customizado
          </Typography>
          <TextField className={classes.field} label="Codigo JS" variant="outlined" size="small" fullWidth multiline rows={8}
            value={localData.code || ""} onChange={(e) => updateField("code", e.target.value)}
            placeholder="// Acesse variaveis via items\n// Retorne resultado via return"
            InputProps={{ style: { fontFamily: "monospace", fontSize: 12 } }} />
          {renderOpenEditorButton()}
        </>
      );
    }

    if (nodeType === "dateTime") {
      return (
        <>
          <Select className={classes.field} variant="outlined" fullWidth
            value={localData.operation || "format"} onChange={(e) => updateField("operation", e.target.value)}
            style={{ marginBottom: 14 }}>
            <MenuItem value="format">Formatar data</MenuItem>
            <MenuItem value="add">Adicionar tempo</MenuItem>
            <MenuItem value="subtract">Subtrair tempo</MenuItem>
            <MenuItem value="diff">Diferenca entre datas</MenuItem>
            <MenuItem value="now">Data/hora atual</MenuItem>
          </Select>
          <TextField className={classes.field} label="Formato de entrada" variant="outlined" size="small" fullWidth
            value={localData.inputFormat || ""} onChange={(e) => updateField("inputFormat", e.target.value)}
            placeholder="YYYY-MM-DD HH:mm:ss" />
          <TextField className={classes.field} label="Formato de saida" variant="outlined" size="small" fullWidth
            value={localData.outputFormat || ""} onChange={(e) => updateField("outputFormat", e.target.value)}
            placeholder="DD/MM/YYYY" />
          <TextField className={classes.field} label="Timezone" variant="outlined" size="small" fullWidth
            value={localData.timezone || ""} onChange={(e) => updateField("timezone", e.target.value)}
            placeholder="America/Sao_Paulo" />
          {localData.operation === "add" || localData.operation === "subtract" ? (
            <>
              <TextField className={classes.field} label="Quantidade" variant="outlined" size="small" fullWidth type="number"
                value={localData.amount || ""} onChange={(e) => updateField("amount", e.target.value)} />
              <Select className={classes.field} variant="outlined" fullWidth
                value={localData.unit || "days"} onChange={(e) => updateField("unit", e.target.value)}
                style={{ marginBottom: 14 }}>
                <MenuItem value="minutes">Minutos</MenuItem>
                <MenuItem value="hours">Horas</MenuItem>
                <MenuItem value="days">Dias</MenuItem>
                <MenuItem value="months">Meses</MenuItem>
                <MenuItem value="years">Anos</MenuItem>
              </Select>
            </>
          ) : null}
        </>
      );
    }

    if (nodeType === "text") {
      return (
        <>
          <Select className={classes.field} variant="outlined" fullWidth
            value={localData.operation || "concat"} onChange={(e) => updateField("operation", e.target.value)}
            style={{ marginBottom: 14 }}>
            <MenuItem value="concat">Concatenar</MenuItem>
            <MenuItem value="split">Dividir (split)</MenuItem>
            <MenuItem value="replace">Substituir</MenuItem>
            <MenuItem value="uppercase">Maiusculas</MenuItem>
            <MenuItem value="lowercase">Minusculas</MenuItem>
            <MenuItem value="trim">Remover espacos</MenuItem>
            <MenuItem value="extract">Extrair substring</MenuItem>
            <MenuItem value="length">Comprimento</MenuItem>
          </Select>
          <TextField className={classes.field} label="Texto / Variavel de entrada" variant="outlined" size="small" fullWidth
            value={localData.inputText || ""} onChange={(e) => updateField("inputText", e.target.value)}
            placeholder="{{variavel}} ou texto fixo" />
          {(localData.operation === "replace" || localData.operation === "split") && (
            <TextField className={classes.field} label="Buscar" variant="outlined" size="small" fullWidth
              value={localData.searchText || ""} onChange={(e) => updateField("searchText", e.target.value)} />
          )}
          {localData.operation === "replace" && (
            <TextField className={classes.field} label="Substituir por" variant="outlined" size="small" fullWidth
              value={localData.replaceText || ""} onChange={(e) => updateField("replaceText", e.target.value)} />
          )}
          {localData.operation === "concat" && (
            <TextField className={classes.field} label="Texto adicional" variant="outlined" size="small" fullWidth
              value={localData.appendText || ""} onChange={(e) => updateField("appendText", e.target.value)} />
          )}
          <TextField className={classes.field} label="Salvar em variavel" variant="outlined" size="small" fullWidth
            value={localData.outputVar || ""} onChange={(e) => updateField("outputVar", e.target.value)} />
        </>
      );
    }

    if (nodeType === "regex") {
      return (
        <>
          <TextField className={classes.field} label="Expressao Regular" variant="outlined" size="small" fullWidth
            value={localData.pattern || ""} onChange={(e) => updateField("pattern", e.target.value)}
            placeholder="^\d{11}$"
            InputProps={{ style: { fontFamily: "monospace" } }} />
          <TextField className={classes.field} label="Flags" variant="outlined" size="small" fullWidth
            value={localData.flags || "i"} onChange={(e) => updateField("flags", e.target.value)}
            helperText="g = global, i = case insensitive, m = multiline" />
          <TextField className={classes.field} label="Texto de entrada" variant="outlined" size="small" fullWidth
            value={localData.inputText || ""} onChange={(e) => updateField("inputText", e.target.value)}
            placeholder="{{variavel}}" />
          <TextField className={classes.field} label="Salvar match em" variant="outlined" size="small" fullWidth
            value={localData.outputVar || ""} onChange={(e) => updateField("outputVar", e.target.value)} />
          <Typography variant="caption" style={{ color: "#999" }}>
            Saidas: match (texto encontrado) / noMatch (nao encontrou)
          </Typography>
          {renderOpenEditorButton()}
        </>
      );
    }

    if (nodeType === "emailSmtp") {
      return (
        <>
          <TextField className={classes.field} label="Servidor SMTP" variant="outlined" size="small" fullWidth
            value={localData.smtpHost || ""} onChange={(e) => updateField("smtpHost", e.target.value)}
            placeholder="smtp.gmail.com" />
          <TextField className={classes.field} label="Porta" variant="outlined" size="small" fullWidth type="number"
            value={localData.smtpPort || 587} onChange={(e) => updateField("smtpPort", e.target.value)} />
          <TextField className={classes.field} label="Usuario" variant="outlined" size="small" fullWidth
            value={localData.smtpUser || ""} onChange={(e) => updateField("smtpUser", e.target.value)} />
          <TextField className={classes.field} label="Senha" variant="outlined" size="small" fullWidth type="password"
            value={localData.smtpPass || ""} onChange={(e) => updateField("smtpPass", e.target.value)} />
          <Divider style={{ margin: "10px 0" }} />
          <TextField className={classes.field} label="De (email)" variant="outlined" size="small" fullWidth
            value={localData.from || ""} onChange={(e) => updateField("from", e.target.value)} />
          <TextField className={classes.field} label="Para (email)" variant="outlined" size="small" fullWidth
            value={localData.to || ""} onChange={(e) => updateField("to", e.target.value)}
            placeholder="dest@email.com ou {{variavel}}" />
          <TextField className={classes.field} label="Assunto" variant="outlined" size="small" fullWidth
            value={localData.subject || ""} onChange={(e) => updateField("subject", e.target.value)} />
          <TextField className={classes.field} label="Corpo do email" variant="outlined" size="small" fullWidth multiline rows={4}
            value={localData.body || ""} onChange={(e) => updateField("body", e.target.value)} />
          <FormControlLabel
            control={<Switch size="small" checked={!!localData.isHtml} onChange={(e) => updateField("isHtml", e.target.checked)} />}
            label={<Typography variant="caption">Corpo em HTML</Typography>} />
          {renderOpenEditorButton()}
        </>
      );
    }

    // ============== Logica ==============
    if (nodeType === "wait") {
      const waitAmount = localData.waitAmount ?? localData.seconds ?? 5;
      const waitUnit = localData.waitUnit || localData.unit || "seconds";
      return (
        <>
          <TextField className={classes.field} label="Tempo de espera" variant="outlined" size="small" fullWidth type="number"
            value={waitAmount} onChange={(e) => updateField("waitAmount", Number(e.target.value))} />
          <Select className={classes.field} variant="outlined" fullWidth
            value={waitUnit} onChange={(e) => updateField("waitUnit", e.target.value)}
            style={{ marginBottom: 14 }}>
            <MenuItem value="seconds">Segundos</MenuItem>
            <MenuItem value="minutes">Minutos</MenuItem>
            <MenuItem value="hours">Horas</MenuItem>
          </Select>
          <FormControlLabel
            control={<Switch size="small" checked={!!localData.waitForInput} onChange={(e) => updateField("waitForInput", e.target.checked)} />}
            label={<Typography variant="caption">Aguardar resposta do contato</Typography>} />
          <Typography variant="caption" style={{ color: "#999", display: "block", marginTop: -8 }}>
            A opção acima ainda não retoma o fluxo automaticamente após a resposta.
          </Typography>
        </>
      );
    }

    if (nodeType === "merge") {
      return (
        <>
          <Select className={classes.field} variant="outlined" fullWidth
            value={localData.mode || "waitAll"} onChange={(e) => updateField("mode", e.target.value)}
            style={{ marginBottom: 14 }}>
            <MenuItem value="waitAll">Aguardar todos (AND)</MenuItem>
            <MenuItem value="waitAny">Aguardar qualquer (OR)</MenuItem>
            <MenuItem value="passthrough">Passthrough</MenuItem>
          </Select>
          <TextField className={classes.field} label="Num. de entradas" variant="outlined" size="small" fullWidth type="number"
            value={localData.inputCount || 2} onChange={(e) => updateField("inputCount", Number(e.target.value))} />
          <Typography variant="caption" style={{ color: "#999" }}>
            Unifica multiplos caminhos em um unico ponto de continuacao.
          </Typography>
        </>
      );
    }

    if (nodeType === "loop") {
      return (
        <>
          <TextField className={classes.field} label="Variavel de lista" variant="outlined" size="small" fullWidth
            value={localData.listVariable || ""} onChange={(e) => updateField("listVariable", e.target.value)}
            placeholder="{{itens}}" />
          <TextField className={classes.field} label="Var. do item atual" variant="outlined" size="small" fullWidth
            value={localData.itemVariable || "item"} onChange={(e) => updateField("itemVariable", e.target.value)} />
          <TextField className={classes.field} label="Var. do indice" variant="outlined" size="small" fullWidth
            value={localData.indexVariable || "index"} onChange={(e) => updateField("indexVariable", e.target.value)} />
          <TextField className={classes.field} label="Limite maximo" variant="outlined" size="small" fullWidth type="number"
            value={localData.maxIterations || 100} onChange={(e) => updateField("maxIterations", Number(e.target.value))}
            helperText="Previne loops infinitos" />
          <Typography variant="caption" style={{ color: "#999" }}>
            Saidas: each (executa para cada item) → done (apos todos)
          </Typography>
          {renderOpenEditorButton()}
        </>
      );
    }

    if (nodeType === "splitBatch") {
      return (
        <>
          <TextField className={classes.field} label="Variavel de lista" variant="outlined" size="small" fullWidth
            value={localData.listVariable || ""} onChange={(e) => updateField("listVariable", e.target.value)}
            placeholder="{{itens}}" />
          <TextField className={classes.field} label="Tamanho do lote" variant="outlined" size="small" fullWidth type="number"
            value={localData.batchSize || 10} onChange={(e) => updateField("batchSize", Number(e.target.value))} />
          <TextField className={classes.field} label="Delay entre lotes (s)" variant="outlined" size="small" fullWidth type="number"
            value={localData.delayBetween || 1} onChange={(e) => updateField("delayBetween", Number(e.target.value))} />
          <Typography variant="caption" style={{ color: "#999" }}>
            Saidas: batch (cada lote) → done (apos todos)
          </Typography>
          {renderOpenEditorButton()}
        </>
      );
    }

    if (nodeType === "stopError") {
      return (
        <>
          <TextField className={classes.field} label="Mensagem de erro" variant="outlined" size="small" fullWidth
            value={localData.errorMessage || ""} onChange={(e) => updateField("errorMessage", e.target.value)}
            placeholder="Erro: condicao nao atendida" />
          <Select className={classes.field} variant="outlined" fullWidth
            value={localData.errorType || "error"} onChange={(e) => updateField("errorType", e.target.value)}
            style={{ marginBottom: 14 }}>
            <MenuItem value="error">Erro (interrompe fluxo)</MenuItem>
            <MenuItem value="warning">Aviso (loga e para)</MenuItem>
          </Select>
          <Typography variant="caption" style={{ color: "#999" }}>
            Interrompe a execucao do fluxo e registra o erro.
          </Typography>
        </>
      );
    }

    if (nodeType === "ifAdvanced") {
      const conditions = localData.conditions || [{ field: "", op: "==", value: "" }];
      return (
        <>
          <Typography variant="caption" style={{ color: "#666", display: "block", marginBottom: 8 }}>
            Condicoes (todas devem ser verdadeiras)
          </Typography>
          {conditions.map((cond, i) => (
            <div key={i} style={{ display: "flex", gap: 4, marginBottom: 8, alignItems: "center" }}>
              <span className={classes.optionNum}>{i + 1}</span>
              <TextField size="small" variant="outlined" placeholder="Campo" style={{ flex: 1 }}
                value={cond.field || ""} onChange={(e) => {
                  const updated = [...conditions]; updated[i] = { ...cond, field: e.target.value };
                  updateField("conditions", updated);
                }} />
              <Select size="small" variant="outlined" value={cond.op || "=="} style={{ width: 70 }}
                onChange={(e) => {
                  const updated = [...conditions]; updated[i] = { ...cond, op: e.target.value };
                  updateField("conditions", updated);
                }}>
                {CONDITION_OPS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
              <TextField size="small" variant="outlined" placeholder="Valor" style={{ flex: 1 }}
                value={cond.value || ""} onChange={(e) => {
                  const updated = [...conditions]; updated[i] = { ...cond, value: e.target.value };
                  updateField("conditions", updated);
                }} />
              {conditions.length > 1 && (
                <IconButton size="small" onClick={() => {
                  const updated = conditions.filter((_, idx) => idx !== i);
                  updateField("conditions", updated);
                }}><DeleteIcon style={{ fontSize: 16 }} /></IconButton>
              )}
            </div>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={() => {
            updateField("conditions", [...conditions, { field: "", op: "==", value: "" }]);
          }}>Adicionar condicao</Button>
          {renderOpenEditorButton()}
        </>
      );
    }

    if (nodeType === "switch") {
      const cases = localData.cases || [{ value: "" }];
      return (
        <>
          <TextField className={classes.field} label="Variavel de entrada" variant="outlined" size="small" fullWidth
            value={localData.switchField || ""} onChange={(e) => updateField("switchField", e.target.value)}
            placeholder="{{variavel}}" />
          <Typography variant="caption" style={{ color: "#666", display: "block", marginBottom: 8 }}>
            Casos (cada valor gera uma saida)
          </Typography>
          {cases.map((c, i) => (
            <div key={i} className={classes.optionRow}>
              <span className={classes.optionNum}>{i + 1}</span>
              <TextField size="small" variant="outlined" placeholder={`Caso ${i + 1}`} fullWidth
                value={c.value || ""} onChange={(e) => {
                  const updated = [...cases]; updated[i] = { ...c, value: e.target.value };
                  updateField("cases", updated);
                }} />
              {cases.length > 1 && (
                <IconButton size="small" onClick={() => {
                  updateField("cases", cases.filter((_, idx) => idx !== i));
                }}><DeleteIcon style={{ fontSize: 16 }} /></IconButton>
              )}
            </div>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={() => {
            updateField("cases", [...cases, { value: "" }]);
          }}>Adicionar caso</Button>
          <Typography variant="caption" style={{ color: "#999", display: "block", marginTop: 8 }}>
            Saida "fallback" para valores nao mapeados
          </Typography>
          {renderOpenEditorButton()}
        </>
      );
    }

    if (nodeType === "filter") {
      return (
        <>
          <TextField className={classes.field} label="Campo" variant="outlined" size="small" fullWidth
            value={localData.filterField || ""} onChange={(e) => updateField("filterField", e.target.value)}
            placeholder="{{variavel}}" />
          <Select className={classes.field} variant="outlined" fullWidth
            value={localData.filterOp || "=="} onChange={(e) => updateField("filterOp", e.target.value)}
            style={{ marginBottom: 14 }}>
            {CONDITION_OPS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            <MenuItem value="contains">Contem</MenuItem>
            <MenuItem value="notContains">Nao contem</MenuItem>
            <MenuItem value="startsWith">Comeca com</MenuItem>
            <MenuItem value="endsWith">Termina com</MenuItem>
            <MenuItem value="isEmpty">Esta vazio</MenuItem>
            <MenuItem value="isNotEmpty">Nao esta vazio</MenuItem>
          </Select>
          <TextField className={classes.field} label="Valor" variant="outlined" size="small" fullWidth
            value={localData.filterValue || ""} onChange={(e) => updateField("filterValue", e.target.value)} />
          <Typography variant="caption" style={{ color: "#999" }}>
            Saidas: true (passou) / false (nao passou)
          </Typography>
          {renderOpenEditorButton()}
        </>
      );
    }

    // ============== IA ==============
    if (nodeType === "aiAgent") {
      return (
        <>
          <Select className={classes.field} variant="outlined" fullWidth
            value={localData.provider || "openai"} onChange={(e) => updateField("provider", e.target.value)}
            style={{ marginBottom: 14 }}>
            <MenuItem value="openai">OpenAI (GPT)</MenuItem>
            <MenuItem value="gemini">Google Gemini</MenuItem>
            <MenuItem value="claude">Anthropic Claude</MenuItem>
          </Select>
          <TextField className={classes.field} label="Modelo" variant="outlined" size="small" fullWidth
            value={localData.model || ""} onChange={(e) => updateField("model", e.target.value)}
            placeholder="gpt-4o-mini" />
          <TextField className={classes.field} label="Prompt do sistema" variant="outlined" size="small" fullWidth multiline rows={4}
            value={localData.systemPrompt || ""} onChange={(e) => updateField("systemPrompt", e.target.value)}
            placeholder="Voce e um assistente de atendimento..." />
          <TextField className={classes.field} label="Mensagem de entrada" variant="outlined" size="small" fullWidth
            value={localData.userMessage || ""} onChange={(e) => updateField("userMessage", e.target.value)}
            placeholder="{{mensagem_contato}}" />
          <TextField className={classes.field} label="Temperatura" variant="outlined" size="small" fullWidth type="number"
            value={localData.temperature ?? 0.7} onChange={(e) => updateField("temperature", Number(e.target.value))}
            inputProps={{ min: 0, max: 2, step: 0.1 }} />
          <TextField className={classes.field} label="Max tokens" variant="outlined" size="small" fullWidth type="number"
            value={localData.maxTokens || 1000} onChange={(e) => updateField("maxTokens", Number(e.target.value))} />
          <TextField className={classes.field} label="Salvar resposta em" variant="outlined" size="small" fullWidth
            value={localData.outputVar || ""} onChange={(e) => updateField("outputVar", e.target.value)}
            placeholder="resposta_ia" />
          <Typography variant="caption" style={{ color: "#999" }}>
            Saidas: success / error
          </Typography>
          {renderOpenEditorButton()}
        </>
      );
    }

    if (nodeType === "textClassifier") {
      const categories = localData.categories || [{ label: "", keywords: "" }];
      return (
        <>
          <TextField className={classes.field} label="Texto de entrada" variant="outlined" size="small" fullWidth
            value={localData.inputText || ""} onChange={(e) => updateField("inputText", e.target.value)}
            placeholder="{{mensagem_contato}}" />
          <Select className={classes.field} variant="outlined" fullWidth
            value={localData.method || "keywords"} onChange={(e) => updateField("method", e.target.value)}
            style={{ marginBottom: 14 }}>
            <MenuItem value="keywords">Palavras-chave</MenuItem>
            <MenuItem value="ai">IA (OpenAI)</MenuItem>
          </Select>
          <Typography variant="caption" style={{ color: "#666", display: "block", marginBottom: 8 }}>
            Categorias (cada uma gera uma saida)
          </Typography>
          {categories.map((cat, i) => (
            <div key={i} style={{ marginBottom: 10, padding: 8, border: "1px solid #eee", borderRadius: 6 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                <span className={classes.optionNum}>{i + 1}</span>
                <TextField size="small" variant="outlined" placeholder="Nome da categoria" fullWidth
                  value={cat.label || ""} onChange={(e) => {
                    const updated = [...categories]; updated[i] = { ...cat, label: e.target.value };
                    updateField("categories", updated);
                  }} />
                {categories.length > 1 && (
                  <IconButton size="small" onClick={() => {
                    updateField("categories", categories.filter((_, idx) => idx !== i));
                  }}><DeleteIcon style={{ fontSize: 16 }} /></IconButton>
                )}
              </div>
              <TextField size="small" variant="outlined" placeholder="Palavras-chave (separadas por virgula)" fullWidth
                value={cat.keywords || ""} onChange={(e) => {
                  const updated = [...categories]; updated[i] = { ...cat, keywords: e.target.value };
                  updateField("categories", updated);
                }} />
            </div>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={() => {
            updateField("categories", [...categories, { label: "", keywords: "" }]);
          }}>Adicionar categoria</Button>
          <Typography variant="caption" style={{ color: "#999", display: "block", marginTop: 8 }}>
            Saida "fallback" para texto nao classificado
          </Typography>
          {renderOpenEditorButton()}
        </>
      );
    }

    // ============== Mensagens ==============
    if (nodeType === "sendSticker") {
      return (
        <>
          <TextField className={classes.field} label="URL do sticker" variant="outlined" size="small" fullWidth
            value={localData.stickerUrl || ""} onChange={(e) => updateField("stickerUrl", e.target.value)}
            placeholder="https://... ou {{variavel}}" />
          <Typography variant="caption" style={{ color: "#999" }}>
            Formatos aceitos: WebP, PNG (sera convertido)
          </Typography>
        </>
      );
    }

    if (nodeType === "sendReaction") {
      return (
        <>
          <TextField className={classes.field} label="Emoji da reacao" variant="outlined" size="small" fullWidth
            value={localData.reactionEmoji || localData.emoji || "👍"}
            onChange={(e) => {
              updateField("reactionEmoji", e.target.value);
              updateField("emoji", e.target.value);
            }}
            helperText="Um unico emoji. Deixe vazio para remover reacao." />
          <TextField className={classes.field} label="ID da mensagem" variant="outlined" size="small" fullWidth
            value={localData.messageId || ""} onChange={(e) => updateField("messageId", e.target.value)}
            placeholder="{{lastMessageId}} ou vazio = ultima" />
        </>
      );
    }

    // ============== Documentacao ==============
    if (nodeType === "stickyNote") {
      return (
        <>
          <TextField className={classes.field} label="Nota" variant="outlined" size="small" fullWidth multiline rows={6}
            value={localData.noteText || ""} onChange={(e) => updateField("noteText", e.target.value)}
            placeholder="Documentacao do fluxo..." />
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Typography variant="caption" style={{ color: "#888" }}>Cor:</Typography>
            <input type="color" className={classes.colorPicker}
              value={localData.noteColor || "#FFFDE7"}
              onChange={(e) => updateField("noteColor", e.target.value)} />
          </div>
          <Typography variant="caption" style={{ color: "#999", marginTop: 8, display: "block" }}>
            Notas adesivas sao apenas visuais. Nao sao executadas pelo fluxo.
          </Typography>
        </>
      );
    }

    // ============== Grupo ==============
    if (nodeType === "group") {
      const GROUP_COLOR_OPTIONS = [
        { key: "blue", label: "Azul", color: "#3b82f6" },
        { key: "green", label: "Verde", color: "#22c55e" },
        { key: "purple", label: "Roxo", color: "#8b5cf6" },
        { key: "orange", label: "Laranja", color: "#f97316" },
        { key: "pink", label: "Rosa", color: "#ec4899" },
        { key: "gray", label: "Cinza", color: "#6b7280" },
        { key: "red", label: "Vermelho", color: "#ef4444" },
        { key: "teal", label: "Teal", color: "#14b8a6" },
      ];
      return (
        <>
          <TextField className={classes.field} label="Nome do grupo" variant="outlined" size="small" fullWidth
            value={localData.label || ""} onChange={(e) => updateField("label", e.target.value)}
            placeholder="Ex: Validacao de dados" />
          <Typography variant="caption" style={{ color: "#888", marginBottom: 8, display: "block" }}>Cor do grupo:</Typography>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
            {GROUP_COLOR_OPTIONS.map((c) => (
              <div
                key={c.key}
                onClick={() => updateField("groupColor", c.key)}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  backgroundColor: c.color,
                  cursor: "pointer",
                  border: localData.groupColor === c.key ? "3px solid #1a1a1a" : "2px solid transparent",
                  transition: "border 0.15s",
                }}
                title={c.label}
              />
            ))}
          </div>
          <Typography variant="caption" style={{ color: "#999", display: "block" }}>
            Grupos sao apenas visuais. Arraste nodes para dentro do grupo para organizar o fluxo.
          </Typography>
        </>
      );
    }

    // ============== Triggers ==============
    if (nodeType === "webhookTrigger") {
      return (
        <>
          <TextField className={classes.field} label="Caminho do webhook" variant="outlined" size="small" fullWidth
            value={localData.webhookPath || ""} onChange={(e) => updateField("webhookPath", e.target.value)}
            placeholder="/meu-webhook" />
          <Select className={classes.field} variant="outlined" fullWidth
            value={localData.webhookMethod || "POST"} onChange={(e) => updateField("webhookMethod", e.target.value)}
            style={{ marginBottom: 14 }}>
            {["GET", "POST", "PUT"].map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
          </Select>
          <TextField className={classes.field} label="Salvar body em" variant="outlined" size="small" fullWidth
            value={localData.bodyVar || "webhookBody"} onChange={(e) => updateField("bodyVar", e.target.value)} />
          <Typography variant="caption" style={{ color: "#999" }}>
            URL completa sera gerada automaticamente ao salvar.
          </Typography>
          {renderOpenEditorButton()}
        </>
      );
    }

    if (nodeType === "scheduleTrigger") {
      return (
        <>
          <Select className={classes.field} variant="outlined" fullWidth
            value={localData.scheduleType || "interval"} onChange={(e) => updateField("scheduleType", e.target.value)}
            style={{ marginBottom: 14 }}>
            <MenuItem value="interval">Intervalo fixo</MenuItem>
            <MenuItem value="cron">Expressao Cron</MenuItem>
            <MenuItem value="daily">Diario</MenuItem>
            <MenuItem value="weekly">Semanal</MenuItem>
          </Select>
          {localData.scheduleType === "cron" && (
            <TextField className={classes.field} label="Expressao Cron" variant="outlined" size="small" fullWidth
              value={localData.cronExpression || ""} onChange={(e) => updateField("cronExpression", e.target.value)}
              placeholder="0 9 * * 1-5"
              helperText="Min Hora Dia Mes DiaSemana" />
          )}
          {localData.scheduleType === "interval" && (
            <>
              <TextField className={classes.field} label="A cada (minutos)" variant="outlined" size="small" fullWidth type="number"
                value={localData.intervalMinutes || 60} onChange={(e) => updateField("intervalMinutes", Number(e.target.value))} />
            </>
          )}
          {localData.scheduleType === "daily" && (
            <TextField className={classes.field} label="Horario" variant="outlined" size="small" fullWidth
              value={localData.dailyTime || "09:00"} onChange={(e) => updateField("dailyTime", e.target.value)}
              placeholder="HH:MM" />
          )}
          <TextField className={classes.field} label="Timezone" variant="outlined" size="small" fullWidth
            value={localData.timezone || "America/Sao_Paulo"} onChange={(e) => updateField("timezone", e.target.value)} />
          {renderOpenEditorButton()}
        </>
      );
    }

    if (nodeType === "tagCheck") {
      const configuredTags = Array.isArray(localData.tags) && localData.tags.length > 0
        ? localData.tags
        : localData.tag?.id
          ? [localData.tag]
          : [];

      return (
        <div>
          <Typography variant="body2" style={{ color: "#666", marginBottom: 10 }}>
            Selecione uma ou mais tags para comparar com o contato. Se possuir
            qualquer uma delas, segue pela saída <strong>TEM</strong>.
          </Typography>

          {configuredTags.length > 0 ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
              {configuredTags.map((tag) => (
                <span
                  key={tag.id}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "4px 10px",
                    borderRadius: 14,
                    background: "#eceff1",
                    fontSize: 12,
                    color: "#37474f",
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: tag.color || "#9e9e9e",
                      display: "inline-block",
                    }}
                  />
                  {tag.name}
                </span>
              ))}
            </div>
          ) : (
            <Typography variant="body2" style={{ color: "#999", marginBottom: 12 }}>
              Nenhuma tag selecionada.
            </Typography>
          )}

          {renderOpenEditorButton()}
        </div>
      );
    }

    // ============== Fallback: mostra botao editor para nodes complexos ==============
    if (COMPLEX_NODES.includes(nodeType)) {
      return (
        <div>
          <Typography variant="body2" style={{ color: "#666", marginBottom: 10 }}>
            Configure este node no editor completo.
          </Typography>
          {renderOpenEditorButton()}
        </div>
      );
    }

    // Nodes desconhecidos ou simples
    return (
      <div>
        <Typography variant="body2" style={{ color: "#999" }}>
          Este node nao possui configuracoes adicionais.
        </Typography>
        {renderOpenEditorButton()}
      </div>
    );
  };

  // ------- Aba 2: Aparencia -------
  const renderAppearance = () => (
    <>
      <div className={classes.field}>
        <Typography
          variant="caption"
          style={{ color: "#888", display: "block", marginBottom: 6 }}
        >
          Cor de fundo
        </Typography>
        <input
          type="color"
          className={classes.colorPicker}
          value={localData.bgColor || "#ffffff"}
          onChange={(e) => updateField("bgColor", e.target.value)}
        />
      </div>

      <TextField
        className={classes.field}
        label="Label personalizado"
        variant="outlined"
        size="small"
        fullWidth
        value={localData.labelOverride || ""}
        onChange={(e) => updateField("labelOverride", e.target.value)}
        helperText="Sobrescreve o titulo exibido no node"
      />
    </>
  );

  // Impede que o ReactFlow capture cliques/eventos neste painel
  const pararPropagacao = useCallback((e) => {
    e.stopPropagation();
  }, []);

  return (
    <div
      className={`${classes.root} ${isOpen ? classes.visible : classes.hidden}`}
      onClick={pararPropagacao}
      onMouseDown={pararPropagacao}
      onPointerDown={pararPropagacao}
    >
      {/* Cabecalho */}
      <div className={classes.header}>
        <span className={classes.headerIcon}>
          {React.cloneElement(meta.icon, {
            style: { fontSize: 22, color: ACCENT },
          })}
        </span>
        <Typography className={classes.headerTitle}>{meta.label}</Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon style={{ fontSize: 18 }} />
        </IconButton>
      </div>

      {/* Abas */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        classes={{ root: classes.tabsRoot, indicator: classes.tabIndicator }}
      >
        <Tab label="Configuracao" className={classes.tab} />
        <Tab label="Aparencia" className={classes.tab} />
      </Tabs>

      {/* Conteudo */}
      <div className={classes.content}>
        {isOpen && (
          <>
            {/* Info basica do node (sempre visivel) */}
            <TextField
              className={`${classes.field} ${classes.readOnly}`}
              label="Tipo"
              variant="outlined"
              size="small"
              fullWidth
              value={meta.label}
              InputProps={{ readOnly: true }}
            />
            <TextField
              className={`${classes.field} ${classes.readOnly}`}
              label="ID"
              variant="outlined"
              size="small"
              fullWidth
              value={node?.id || ""}
              InputProps={{ readOnly: true }}
            />

            <Divider style={{ marginBottom: 14 }} />

            {/* Conteudo da aba selecionada */}
            {tab === 0 && renderConfig()}
            {tab === 1 && renderAppearance()}
          </>
        )}
      </div>
    </div>
  );
}
